"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { ChevronLeft, ChevronRight, Loader2, ChevronDown } from "lucide-react"
import type { ClawHubSkill, SkillCategoryItem } from "@/lib/clawhub-api"
import { ScenarioMarquee } from "./scenario-marquee"
import { SkillCard } from "./skill-card"

interface SkillsHeroSectionProps {
  hasSearched: boolean
  popularTags: SkillCategoryItem[]
  onTagClick: (tagValue: string) => void
  heroTopRef: React.Ref<HTMLDivElement>
  /** Actual search bar element rendered inline (sticky) */
  searchBar: React.ReactNode
  /** Recommended skills list */
  recommendedSkills: ClawHubSkill[]
  recSentinelRef: React.Ref<HTMLDivElement>
  recLoadingMore: boolean
  recHasMore: boolean
  onLoadMoreRecommended: () => void
  onSkillClick: (skill: ClawHubSkill) => void
  onSkillUpdate?: (updated: ClawHubSkill) => void
}

const TAG_SCROLL_STEP_PX = 160

/**
 * SkillsHeroSection — hero area with title, search placeholder, tags carousel,
 * feature pillars, scenario marquee, and recommended skills grid.
 * Manages its own horizontal scroll state for the tags row.
 */
export function SkillsHeroSection({
  hasSearched,
  popularTags,
  onTagClick,
  heroTopRef,
  searchBar,
  recommendedSkills,
  recSentinelRef,
  recLoadingMore,
  recHasMore,
  onLoadMoreRecommended,
  onSkillClick,
  onSkillUpdate,
}: SkillsHeroSectionProps) {
  const tagsScrollRef = useRef<HTMLDivElement>(null)
  const [tagsCanScrollLeft, setTagsCanScrollLeft] = useState(false)
  const [tagsCanScrollRight, setTagsCanScrollRight] = useState(false)

  const updateTagsScrollState = useCallback(() => {
    const el = tagsScrollRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    const canScroll = scrollWidth > clientWidth + 2
    setTagsCanScrollLeft(canScroll && scrollLeft > 2)
    setTagsCanScrollRight(canScroll && scrollLeft < scrollWidth - clientWidth - 2)
  }, [])

  useEffect(() => {
    if (hasSearched) return
    updateTagsScrollState()
    const el = tagsScrollRef.current
    if (!el) return
    el.addEventListener("scroll", updateTagsScrollState, { passive: true })
    const ro = new ResizeObserver(updateTagsScrollState)
    ro.observe(el)
    return () => {
      el.removeEventListener("scroll", updateTagsScrollState)
      ro.disconnect()
    }
  }, [hasSearched, popularTags, updateTagsScrollState])

  return (
    <>
      {/* items-stretch + w-full：英雄区要占满宽；未搜索时英雄块自身 h-svh 居中，不被压成内容高 */}
      <div className="flex w-full flex-col items-stretch">
        {/* 英雄区：始终渲染，通过 CSS class 切换动画过渡 */}
        <div
          className={`skills-hero-area w-full px-4 sm:px-6 ${hasSearched ? "skills-hero-area--collapsed" : ""
            }`}
        >
          {/* 第 1 行 — 标题区，搜索后由父级 CSS 收起 */}
          <div className="skills-hero-grid__top">
            <div ref={heroTopRef} className="flex w-full max-w-[min(96vw,720px)] flex-col items-center text-center" style={{ willChange: "transform" }}>
              <h1 className="skills-hero__title">
                技能市场
              </h1>
              <p className="skills-hero__subtitle">
                SkillHub
              </p>
              <p className="skills-hero__desc">
                告别繁琐的提示词工程，一键获取经过实战检验的 AI 技能，<br className="hidden sm:inline" />
                开箱即用，即装即上线。
              </p>
            </div>
          </div>

          {/* 第 2 行 — 搜索框（sticky，始终贴在内容区顶部可见） */}
          <div className="skills-hero-grid__mid">
            <div className="skills-search-sticky">
              {searchBar}
            </div>
          </div>

          {/* 第 3 行 — 标签区，搜索后由父级 CSS 收起 */}
          <div className="skills-hero-grid__bottom">
            {/* Tags — 紧凑高度，首屏多露出下方区块 */}
            <div
              className="mt-3 w-full pb-0 opacity-100 transition-all duration-500 ease-out"
            >
              {/* 横向滚动无滚动条；箭头在容器外侧贴右 */}
              {/* 左右对称占位 + 中间滚动：未溢出时 pills 视觉居中，溢出时仍可滑 */}
              <div
                className={`flex flex-row items-center justify-center gap-1 w-full ${hasSearched ? "hidden" : ""
                  }`}
              >
                {/* 左箭头：可向左滚时显示，与右侧对称 */}
                <div className="flex h-6 w-6 shrink-0 items-center justify-center self-center">
                  {tagsCanScrollLeft ? (
                    <button
                      type="button"
                      aria-label="向左滑动标签"
                      className="skills-scroll-arrow skills-scroll-arrow--sm"
                      onClick={() => {
                        tagsScrollRef.current?.scrollBy({
                          left: -TAG_SCROLL_STEP_PX,
                          behavior: "smooth",
                        })
                      }}
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <span className="block h-6 w-6" aria-hidden />
                  )}
                </div>
                <div className="relative min-w-0 max-w-full flex-1 overflow-hidden rounded-lg">
                  <div
                    ref={tagsScrollRef}
                    className="no-scrollbar overflow-x-auto overflow-y-hidden py-0 px-1 mx-0 skills-scrollable"
                  >
                    {/* min-w-full + justify-center：未溢出时 pills 在条内居中；溢出时 inline-flex 随内容变宽，scrollLeft=0 仍从左侧滑 */}
                    <div className="inline-flex min-w-full justify-center">
                      <div className="flex shrink-0 flex-nowrap items-center gap-2 w-max">
                        {popularTags.map((tag) => (
                          <button
                            key={tag.id}
                            onClick={() => onTagClick(tag.value)}
                            className="skills-tag-pill"
                          >
                            {tag.label || tag.value}
                            <span className="skills-tag-pill__count">
                              {tag.count}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* 左右内侧渐变，提示还可滚 */}
                  <div
                    className={`skills-fade-gradient skills-fade-gradient--left ${tagsCanScrollLeft ? "opacity-100" : "opacity-0"
                      }`}
                    aria-hidden
                  />
                  <div
                    className={`skills-fade-gradient skills-fade-gradient--right ${tagsCanScrollRight ? "opacity-100" : "opacity-0"
                      }`}
                    aria-hidden
                  />
                </div>
                {/* 右侧箭头；左侧同等占位，整行相对视口居中 */}
                <div className="flex h-6 w-6 shrink-0 items-center justify-center self-center">
                  {tagsCanScrollRight ? (
                    <button
                      type="button"
                      aria-label="向右滑动标签"
                      className="skills-scroll-arrow skills-scroll-arrow--sm"
                      onClick={() => {
                        tagsScrollRef.current?.scrollBy({
                          left: TAG_SCROLL_STEP_PX,
                          behavior: "smooth",
                        })
                      }}
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <span className="block h-6 w-6" aria-hidden />
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Feature Pillars — 暂时隐藏但保留占位 */}
        {!hasSearched && (
          <div className="w-full max-w-[min(96vw,920px)] mx-auto px-4 sm:px-6 pt-4 pb-1 invisible">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {([
                { label: "效率", title: "开箱即用", desc: "省去数周的提示词调试，直接上手", accent: "amber" },
                { label: "可靠", title: "实战验证", desc: "源自真实生产环境的成熟配置", accent: "emerald" },
                { label: "便捷", title: "即装即用", desc: "几分钟完成安装，立刻投入使用", accent: "blue" },
              ] as const).map((f) => (
                <div key={f.title} className={`skills-pillar skills-pillar--${f.accent}`}>
                  <div className={`skills-pillar__stripe skills-pillar__stripe--${f.accent}`} />
                  <div className="flex-1 min-w-0 py-4 pl-4 pr-4">
                    <span className={`skills-pillar__label skills-pillar__label--${f.accent}`}>
                      {f.label}
                    </span>
                    <h3 className="skills-pillar__title">{f.title}</h3>
                    <p className="skills-pillar__desc">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scrolling Scenario Marquee — EasyClaw-inspired */}
        {!hasSearched && (
          <>
            <div className="w-full max-w-[min(96vw,1200px)] mx-auto px-4 sm:px-6 text-center mt-2">
              <h2 className="skills-section-heading">
                一个指令，千种场景
              </h2>
              <p className="skills-section-subtitle">
                描述你的目标，多 Agent 自动分工协作，从数据采集到交付一站式完成。
              </p>
            </div>
            <ScenarioMarquee />
          </>
        )}

        {/* 热门推荐 */}
        {!hasSearched && (
          <div className="w-full max-w-[min(96vw,1200px)] mx-auto px-4 pb-10 sm:px-6 transition-all duration-500 ease-out mt-4">
            <div className="mb-6">
              <h2 className="skills-section-heading skills-section-heading--sm">热门技能</h2>
              <p className="skills-section-subtitle skills-section-subtitle--tight">SafeClaw 生态中最受欢迎的 AI 技能</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {recommendedSkills.map((skill, i) => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  onClick={() => onSkillClick(skill)}
                  onTagClick={onTagClick}
                  delay={Math.min(i * 40, 400)}
                  compact
                  onSkillUpdate={onSkillUpdate}
                />
              ))}
            </div>
            <div ref={recSentinelRef} className="h-4 w-full" aria-hidden />
            <div className="flex flex-col items-center gap-3 mt-6 pb-2">
              {recLoadingMore && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="skills-loading__spinner--sm animate-spin" />
                  加载中…
                </div>
              )}
              {recHasMore && !recLoadingMore && (
                <button
                  type="button"
                  onClick={onLoadMoreRecommended}
                  className="skills-load-more-btn"
                >
                  <ChevronDown className="h-4 w-4" />
                  加载更多
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
